import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { composeInitialProps, useTranslation } from "react-i18next";
import { RestAPI } from "@aws-amplify/api-rest";
import SelfHelp from "../Utils/SelfHelp";
import withName from "../../hoc/withName";
import LoadingDialog from "../Utils/LoadingDialog";
import { GoogleMapsFarm } from "../GoogleMapsFarm/GoogleMapsFarm";
import PromiseWrapper from "../Utils/PromiseDialog";
import axios from "axios";
import { toast } from "react-toastify";
import { FarmContext } from "../../contexts/FarmContext";
import ButtonPrime from "../PrimeComponents/ButtonPrime";
import TooltipPrime from "../PrimeComponents/TooltipPrime";

const getExternal = (location) => {
    const params = new URLSearchParams(location && location.search);
    const external = params.get("external");
    return external;
};

const getFarm = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get("farm");
    return farm;
};

const ImageAligner = ({ dataFarm, nodesInfo, sectorsInfo, mapRef, isLoaded }) => {
    const { t, i18n } = useTranslation();
    const [dataLoading, setDataLoading] = useState(true);
    const location = useLocation();
    const externalId = getExternal(location);
    const farmId = getFarm(location);
    const navigate = useNavigate();
    const [externalData, setExternalData] = useState(null);
    const [finalBounds, setFinalBounds] = useState(null);
    const promiseW = useRef();
    const [imageInfo, setImageInfo] = useState(null);
    const [_, dispatch] = useContext(FarmContext);
    const acceso_limitado = dispatch({ type: "get", path: "accesoLimitado" });

    const selfHelpImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-aligner-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-aligner-1.png`];
    const selfHelpImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-aligner-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-aligner-2.png`];
    const selfHelpImg3 = import.meta.glob("../../img/autoayuda/*/autoayuda-aligner-3.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-aligner-3.png`];
    const selfHelpImg4 = import.meta.glob("../../img/autoayuda/*/autoayuda-aligner-4.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-aligner-4.png`];
    const selfHelpImg5 = import.meta.glob("../../img/autoayuda/*/autoayuda-aligner-5.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-aligner-5.png`];

    const selfHelp = [
        {
            tab: 1, header: { text: t("selfHelp.imagealigner.header") }, params:
                [
                    { id: 1, text: t("selfHelp.imagealigner.msg1"), image: selfHelpImg1 },
                    { id: 2, text: t("selfHelp.imagealigner.msg2"), image: selfHelpImg2 },
                    { id: 3, text: t("selfHelp.imagealigner.msg3"), image: selfHelpImg3 },
                    { id: 4, text: t("selfHelp.imagealigner.msg4"), image: selfHelpImg4 },
                    { id: 5, text: t("selfHelp.imagealigner.msg5"), image: selfHelpImg5 },
                    { id: 6, text: t("selfHelp.imagealigner.msg6"), image: [] },
                ]
        }
    ];

    const checkImage = async (url) => {
        return new Promise(res => {
            const img = new Image();
            img.onload = () => res(true);
            img.onerror = () => res(false);
            img.src = url;
        });
    };

    useEffect(() => {
        !dataLoading && setDataLoading(true);
        if (externalData === null && farmId && externalId && !acceso_limitado) {
            const param = `/external/${farmId}/proveedor/${externalId}`;
            const getData = async () => {
                await RestAPI.get("apiAdminDropControl", param).then(async (res) => {
                    //console.log(res);
                    const r = JSON.parse(JSON.stringify(res));
                    setExternalData(r);
                    if (r.img !== null) {
                        const validImage = await checkImage(r.img.ndvi_raw);
                        if (validImage && r.img.bounds_raw) {
                            setImageInfo({
                                image: r.img.ndvi_raw,
                                bounds: r.img.bounds_raw
                            });
                        }
                    } else {
                        setImageInfo(null);
                        toast.error(t("forms.toastErrorAligner"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                    }
                }).catch((err) => {
                    console.log(err);
                    setExternalData(null);
                    setImageInfo(null);
                    toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                });
            };
            getData();
        }
        setTimeout(() => {
            setDataLoading(false);
        }, 3000);
    }, [externalData]);

    const saveOffset = async () => {
        if (finalBounds) {
            !dataLoading && setDataLoading(true);
            const answer = await promiseW.current.getPromiseAnswer();
            //console.log(answer);
            const sw = new window.google.maps.LatLng(imageInfo.bounds.south, imageInfo.bounds.west);
            const ne = new window.google.maps.LatLng(imageInfo.bounds.north, imageInfo.bounds.east);
            //console.log("Initial imageInfo.bounds", [[sw.lat(), sw.lng()], [ne.lat(), ne.lng()]]);
            //console.log("final imageInfo.bounds", [[finalBounds.getSouthWest().lat(), finalBounds.getSouthWest().lng()], [finalBounds.getNorthEast().lat(), finalBounds.getNorthEast().lng()]])
            const offset = {
                lat: imageInfo.bounds.south - finalBounds.getSouthWest().lat(),
                lng: imageInfo.bounds.west - finalBounds.getSouthWest().lng(),
                recalc: answer
            };
            const param = `/external/${farmId}/proveedor/${externalId}`;

            RestAPI.post("apiAdminDropControl", param, { body: offset }).then(res => {
                toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                setTimeout(() => {
                    const newSearch = `?farm=${farmId}`;
                    if (location.state?.redirectLink){
                        navigate({ pathname: location.state.redirectLink, search: newSearch },
                            {
                                state: {
                                    expanded: ["external"],
                                    updateTree: true
                                }
                            });
                    } else {
                        navigate({ pathname: "/farms/external", search: newSearch },
                            {
                                state: {
                                    expanded: ["external"],
                                    updateTree: true
                                }
                            });
                    }
                }, 3000);
            }).catch(error => {
                console.log(error);
                toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                setDataLoading(false);
            });
            /*
            const newSearch = `?farm=${farmId}`;
            navigate({ pathname: "/farms/external", search: newSearch },
                {
                    state: {
                        expanded: ["external"],
                        updateTree: true
                    }
                });*/
        } else {
            toast.info(t("forms.toastInfo"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
        }
    };

    return (
        <div id='aligner-container' className='row-container'>
            {dataLoading && <LoadingDialog appendTo="self" />}
            <PromiseWrapper ref={promiseW} yesText={t("general.answerYes")} noText={t("general.answerNo")}
                headerText={t("external.alignerHeaderPromise")}
                contentText={t("external.alignerTextPromise")}
            />
            {!acceso_limitado &&
            <div className='row'>
                <div className="col-xl-12 col-lg-12 col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-2'>
                                    {selfHelp &&
                                        <SelfHelp t={t} lng={i18n.language} params={selfHelp} fertiTable />}
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-8 text-center'>
                                    <h5 className="card-title">
                                        <span>{t("external.alinear_img")}</span>
                                    </h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-2 d-flex justify-content-end align-items-center'>
                                    {/*
                                    <TooltipPrime id="toolDownload"  content={t("downloadTooltip")} position="bottom">
                                        <FontAwesomeIcon className='pointer' onClick={() => redirectToMapaConfig()} size="2x" icon={faCog} style={{ color: 'lightblue' }}></FontAwesomeIcon>
                                    </TooltipPrime>*/}
                                </div>
                            </div>
                            <div className='linea-titulo' />
                            {imageInfo && imageInfo.bounds && imageInfo.image &&
                                <div className='mapsWrapper'>
                                    <GoogleMapsFarm
                                        apiKey="AIzaSyCvs7xPDh2vfmQO2w02lcItOXa6OKOVmQ4"
                                        data={dataFarm.mapa_json}
                                        farmId={dataFarm.campo_id}
                                        innerRef={mapRef}
                                        language={i18n.language}
                                        iconBorder={null}
                                        husoHorario={dataFarm.huso_horario}
                                        isImageAligner
                                        setFinalBounds={setFinalBounds}
                                        imageInfo={imageInfo}
                                    />
                                </div>}
                            <div className='linea-titulo' />
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-2'>
                                    {finalBounds && <ButtonPrime label={t("forms.save")} onClick={saveOffset} />}
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-8 text-center' />
                                <div className='col col-sm-2 col-md-3 col-lg-2 justify-content-end align-items-center' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
        </div>
    );


};

export default withName(ImageAligner, "ImageAlignerFrag");

